import { getSharedState } from "./windowBus";

// Framework-agnostic queue backing showToast() (toast.ts). Kept separate
// from the React renderer (src/react/features/shared/ToastHost.tsx) so
// vanilla call sites can keep importing toast.ts with zero changes while
// only ToastHost.tsx pulls in React.
//
// Entirely windowBus-backed, not module-level state - app.js and
// react-app.js are separately-bundled IIFEs (see windowBus.ts), so a
// plain `let items = []` here would be duplicated per bundle: vanilla
// code (compiled into app.js) would push into its own private copy that
// ToastHost.tsx (compiled into react-app.js) could never see.
export type ToastKind = "success" | "error" | "info";

export interface ToastItem {
  id: number;
  message: string;
  kind: ToastKind;
  leaving: boolean;
}

interface ToastBusState {
  items: ToastItem[];
  listeners: Set<() => void>;
  timers: Map<number, ReturnType<typeof setTimeout>>;
  nextId: number;
}

function state(): ToastBusState {
  return getSharedState<ToastBusState>("__toastBusState", () => ({
    items: [],
    listeners: new Set(),
    timers: new Map(),
    nextId: 1,
  }));
}

function notify(): void {
  state().listeners.forEach((fn) => fn());
}

export function subscribeToasts(fn: () => void): () => void {
  state().listeners.add(fn);
  return () => state().listeners.delete(fn);
}

export function getToasts(): ToastItem[] {
  return state().items;
}

// Two-phase removal (mark leaving, then actually remove 300ms later)
// mirrors the original DOM implementation's fade-out-then-remove so the
// CSS transition still plays instead of the toast just vanishing.
export function dismissToast(id: number): void {
  const s = state();
  const timer = s.timers.get(id);
  if (timer) {
    clearTimeout(timer);
    s.timers.delete(id);
  }

  const item = s.items.find((t) => t.id === id);
  if (!item || item.leaving) return;

  s.items = s.items.map((t) => (t.id === id ? { ...t, leaving: true } : t));
  notify();

  setTimeout(() => {
    const s2 = state();
    s2.items = s2.items.filter((t) => t.id !== id);
    notify();
  }, 300);
}

export function pushToast(message: string, kind: ToastKind = "info", durationMs = 3200): () => void {
  const s = state();
  const id = s.nextId++;
  s.items = [...s.items, { id, message, kind, leaving: false }];
  notify();

  s.timers.set(
    id,
    setTimeout(() => dismissToast(id), durationMs),
  );

  return () => dismissToast(id);
}
